import React, { useRef, useState } from 'react';
import {
  Button, Input, message, Tooltip,
} from 'antd';
import {
  DeleteOutlined, PlusOutlined, CopyOutlined, LinkOutlined, EditOutlined,
} from '@ant-design/icons';
import I18n from 'i18n-js';
import { arrayOf, string, func } from 'prop-types';

const parseDomain = (url) => {
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

const faviconUrl = (url) => {
  const domain = parseDomain(url);
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?sz=32&domain=${domain}`;
};

const ExternalLinks = ({ links, onChange }) => {
  const [editingIndex, setEditingIndex] = useState(null);
  const inputRef = useRef(null);

  const handleAdd = () => {
    const newIndex = links.length;
    onChange([...links, '']);
    setEditingIndex(newIndex);
  };

  const handleRemove = (index) => {
    onChange(links.filter((_, i) => i !== index));
    setEditingIndex(null);
  };

  const handleChange = (index, value) => (
    onChange(links.map((link, i) => (i === index ? value : link)))
  );

  const handleCopy = (url) => {
    navigator.clipboard.writeText(url);
    message.success(I18n.t('tickets.form.link_copied'));
  };

  const handleConfirm = (index) => {
    if (links[index]?.trim()) {
      setEditingIndex(null);
    } else {
      handleRemove(index);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Enter') handleConfirm(index);
    if (e.key === 'Escape') handleRemove(index);
  };

  return (
    <div className="tickets-index-page__external-links">
      {links.map((url, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={`external-link-${index}`}>
          {editingIndex === index ? (
            <div className="tickets-index-page__external-link-input">
              <Input
                ref={inputRef}
                placeholder="https://..."
                value={url}
                onChange={(e) => handleChange(index, e.target.value)}
                onBlur={() => handleConfirm(index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                prefix={<LinkOutlined />}
                autoFocus
              />
            </div>
          ) : (
            <div
              className="tickets-index-page__external-link-card"
              onDoubleClick={() => setEditingIndex(index)}
            >
              {faviconUrl(url) ? (
                <img
                  className="tickets-index-page__external-link-card__favicon"
                  src={faviconUrl(url)}
                  alt=""
                />
              ) : (
                <LinkOutlined className="tickets-index-page__external-link-card__icon" />
              )}

              <Tooltip title={url}>
                <a
                  className="tickets-index-page__external-link-card__url"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {parseDomain(url) || url}
                </a>
              </Tooltip>

              <div className="tickets-index-page__external-link-card__actions">
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setEditingIndex(index)}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => handleCopy(url)}
                />
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemove(index)}
                />
              </div>
            </div>
          )}
        </div>
      ))}

      <Button
        type="dashed"
        onClick={handleAdd}
        icon={<PlusOutlined />}
        block
        className="tickets-index-page__external-link-add-btn"
      >
        {I18n.t('tickets.form.add_link')}
      </Button>
    </div>
  );
};

ExternalLinks.propTypes = {
  links: arrayOf(string).isRequired,
  onChange: func.isRequired,
};

export default ExternalLinks;
